import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminAuthService } from '../../../../shared/services/adminAuth'

interface Plan {
  id: string
  name: string
  type: string
  displayName: string
  description: string
  price: {
    monthly: number
    yearly: number
  }
  features: {
    linksPerMonth: number
    clicksPerMonth: number
    customDomains: number
    teamMembers: number
    analytics: boolean
    apiAccess: boolean
  }
  popular?: boolean
  badge?: string
  color?: string
}

interface User {
  _id: string
  email: string
  name?: string
  plan: string
  monthlyLinkLimit: number
  monthlyClickLimit: number
}

interface AdminPlanChangeModalProps {
  isOpen: boolean
  onClose: () => void
  user: User
  onSuccess: () => void
}

export default function AdminPlanChangeModal({ isOpen, onClose, user, onSuccess }: AdminPlanChangeModalProps) {
  const [plans, setPlans] = useState<Plan[]>([])
  const [selectedPlan, setSelectedPlan] = useState<string>('')
  const [reason, setReason] = useState('')
  const [notifyUser, setNotifyUser] = useState(true)
  const [loading, setLoading] = useState(false)
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)

  // Load available plans
  useEffect(() => {
    if (isOpen) {
      loadPlans()
      setSelectedPlan(user.plan) // Set current plan as default
    }
  }, [isOpen, user.plan])

  const loadPlans = async () => {
    try {
      setLoadingPlans(true)
      const response = await fetch('/api/admin/plan-management/plans', {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch plans')
      }
      
      const data = await response.json()
      setPlans(data.data.plans)
    } catch (error) {
      console.error('Failed to load plans:', error)
      toast.error('Failed to load plans')
    } finally {
      setLoadingPlans(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan')
      return
    }

    if (selectedPlan === user.plan) {
      toast.error('User is already on this plan')
      return
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for the plan change')
      return
    }

    setShowConfirmation(true)
  }

  const handleConfirmChange = async () => {
    try {
      setLoading(true)
      
      const response = await fetch('/api/admin/plan-management/change-plan', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: user._id,
          newPlanType: selectedPlan,
          reason: reason.trim(),
          notifyUser
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to change plan')
      }

      const data = await response.json()
      toast.success(`Plan changed successfully from ${data.data.planChange.oldPlan} to ${data.data.planChange.newPlan}`)
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Failed to change plan:', error)
      toast.error(error.message || 'Failed to change plan')
    } finally {
      setLoading(false)
      setShowConfirmation(false)
    }
  }

  const resetModal = () => {
    setSelectedPlan(user.plan)
    setReason('')
    setNotifyUser(true)
    setShowConfirmation(false)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const getSelectedPlanData = () => plans.find(p => p.type === selectedPlan)
  const getCurrentPlanData = () => plans.find(p => p.type === user.plan)

  const formatNumber = (num: number) => {
    if (num === -1 || num >= 999999) {return 'Unlimited'}
    return num.toLocaleString()
  }

  const getPlanColor = (planType: string) => {
    const colors = {
      free: 'bg-gray-100 text-gray-800 border-gray-300',
      growth: 'bg-blue-100 text-blue-800 border-blue-300',
      scale: 'bg-purple-100 text-purple-800 border-purple-300',
      enterprise: 'bg-green-100 text-green-800 border-green-300'
    }
    return colors[planType as keyof typeof colors] || colors.free
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-lg bg-white dark:bg-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <UserIcon className="h-6 w-6 text-gray-500 mr-3" />
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Change Plan - {user.name || user.email}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Current plan: <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(user.plan)}`}>
                      {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {loadingPlans ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading plans...</p>
                </div>
              ) : showConfirmation ? (
                /* Confirmation Step */
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-1" />
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Confirm Plan Change
                        </h3>
                        <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                          This will immediately change the user's plan and update their limits.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Plan Change Summary */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                      Plan Change Summary
                    </h4>
                    
                    <div className="flex items-center justify-between py-2">
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">From:</span>
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(user.plan)}`}>
                          {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                        </span>
                      </div>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">To:</span>
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(selectedPlan)}`}>
                          {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)}
                        </span>
                      </div>
                    </div>

                    {/* Limits Comparison */}
                    {(() => {
                      const currentPlan = getCurrentPlanData()
                      const newPlan = getSelectedPlanData()
                      
                      if (currentPlan && newPlan) {
                        return (
                          <div className="mt-4 space-y-2 text-sm">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="text-gray-600 dark:text-gray-400">Feature</div>
                              <div className="text-gray-600 dark:text-gray-400">Current</div>
                              <div className="text-gray-600 dark:text-gray-400">New</div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>Monthly Links</div>
                              <div>{formatNumber(currentPlan.features.linksPerMonth)}</div>
                              <div className="font-medium">{formatNumber(newPlan.features.linksPerMonth)}</div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>Monthly Clicks</div>
                              <div>{formatNumber(currentPlan.features.clicksPerMonth)}</div>
                              <div className="font-medium">{formatNumber(newPlan.features.clicksPerMonth)}</div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                              <div>Custom Domains</div>
                              <div>{formatNumber(currentPlan.features.customDomains)}</div>
                              <div className="font-medium">{formatNumber(newPlan.features.customDomains)}</div>
                            </div>
                          </div>
                        )
                      }
                      return null
                    })()}

                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm">
                        <strong>Reason:</strong> {reason}
                      </div>
                      <div className="mt-1 text-sm">
                        <strong>Notify User:</strong> {notifyUser ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowConfirmation(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500 dark:hover:bg-gray-700"
                      disabled={loading}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleConfirmChange}
                      disabled={loading}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Changing Plan...
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Confirm Plan Change
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              ) : (
                /* Plan Selection Step */
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  {/* Plan Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Select New Plan
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {plans.map((plan) => {
                        const isSelected = selectedPlan === plan.type
                        const isCurrent = plan.type === user.plan
                        
                        return (
                          <motion.div
                            key={plan.id}
                            whileHover={{ scale: isCurrent ? 1 : 1.02 }}
                            whileTap={{ scale: isCurrent ? 1 : 0.98 }}
                            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                                : isCurrent
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 opacity-60 cursor-not-allowed'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                            onClick={() => !isCurrent && setSelectedPlan(plan.type)}
                          >
                            {isCurrent && (
                              <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                                Current
                              </div>
                            )}
                            
                            <div className="text-center">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {plan.displayName}
                              </h3>
                              {plan.description && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {plan.description}
                                </p>
                              )}
                              <div className="mt-2">
                                <span className="text-xl font-bold text-gray-900 dark:text-white">
                                  ${plan.price.monthly}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">/mo</span>
                              </div>
                            </div>

                            <div className="mt-4 space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Links:</span>
                                <span className="font-medium">{formatNumber(plan.features.linksPerMonth)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Clicks:</span>
                                <span className="font-medium">{formatNumber(plan.features.clicksPerMonth)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Domains:</span>
                                <span className="font-medium">{formatNumber(plan.features.customDomains)}</span>
                              </div>
                            </div>

                            {plan.popular && (
                              <div className="absolute -top-2 -left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                Popular
                              </div>
                            )}

                            {isSelected && !isCurrent && (
                              <div className="absolute inset-0 border-2 border-indigo-500 rounded-lg pointer-events-none">
                                <div className="absolute -top-2 -right-2 bg-indigo-500 text-white text-xs px-2 py-1 rounded-full">
                                  Selected
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Reason Input */}
                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reason for Plan Change *
                    </label>
                    <textarea
                      id="reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Customer requested upgrade, Special promotion, etc."
                    />
                  </div>

                  {/* Options */}
                  <div>
                    <div className="flex items-center">
                      <input
                        id="notify-user"
                        type="checkbox"
                        checked={notifyUser}
                        onChange={(e) => setNotifyUser(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="notify-user" className="ml-2 text-sm text-gray-900 dark:text-white">
                        Send email notification to user
                      </label>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-600 dark:text-gray-200 dark:border-gray-500 dark:hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={!selectedPlan || selectedPlan === user.plan || !reason.trim()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowRightIcon className="h-4 w-4 mr-2" />
                      Continue
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  )
}
